"use client";

import { useCallback, useEffect, useState } from "react";
import type { JobStatus } from "@easyshot/shared";
import { AdminAuthError, adminFetch } from "@/lib/admin-client";
import { AdminKeyGate } from "@/components/admin-key-gate";
import { Badge, Button, Card, Spinner } from "@/components/ui";

interface AdminJob {
  id: string;
  projectId: string;
  projectName: string;
  status: JobStatus;
  progress: number;
  platform: string;
  avatarId: string;
  style: string;
  mode: "mock" | "real";
  varyHook: boolean;
  heygenVideoId: string | null;
  templateName: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

interface JobsResponse {
  stats: {
    total: number;
    queued: number;
    active: number;
    succeeded: number;
    failed: number;
  };
  jobs: AdminJob[];
}

const TONE: Record<JobStatus, "neutral" | "warning" | "success" | "danger"> = {
  queued: "neutral",
  scripting: "warning",
  rendering: "warning",
  succeeded: "success",
  failed: "danger",
};

export default function AdminJobsPage() {
  const [data, setData] = useState<JobsResponse | null>(null);
  const [needKey, setNeedKey] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await adminFetch<JobsResponse>("/api/admin/jobs"));
      setNeedKey(false);
    } catch (e) {
      if (e instanceof AdminAuthError) setNeedKey(true);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  async function act(jobId: string, action: "cancel" | "requeue") {
    setBusy(jobId);
    setNotice(null);
    try {
      await adminFetch(`/api/admin/jobs/${jobId}`, {
        method: "POST",
        body: JSON.stringify({ action }),
      });
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(null);
    }
  }

  async function runSweep() {
    setBusy("sweep");
    setNotice(null);
    try {
      const r = await adminFetch<{ requeued: number; polled: number }>(
        "/api/admin/sweep",
        { method: "POST" },
      );
      setNotice(`Sweep done — requeued ${r.requeued}, polled ${r.polled}`);
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Sweep failed");
    } finally {
      setBusy(null);
    }
  }

  if (needKey) return <AdminKeyGate onUnlock={() => void load()} />;

  if (!data) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-muted">
        <Spinner className="size-6" />
      </div>
    );
  }

  const tiles: [string, number][] = [
    ["Total", data.stats.total],
    ["Queued", data.stats.queued],
    ["Active", data.stats.active],
    ["Succeeded", data.stats.succeeded],
    ["Failed", data.stats.failed],
  ];

  return (
    <div className="mx-auto max-w-6xl px-5 py-8 sm:py-10">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Job log</h1>
          <p className="mt-2 text-sm text-muted">
            Every render job across all projects. Auto-refreshes every 3s.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy === "sweep"}
          onClick={() => void runSweep()}
        >
          {busy === "sweep" ? <Spinner /> : "Run sweep now"}
        </Button>
      </div>

      {notice && (
        <Card className="mb-4 px-4 py-3 text-[13px] text-muted">{notice}</Card>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {tiles.map(([label, value]) => (
          <Card key={label} className="px-4 py-3">
            <div className="text-xl font-semibold">{value}</div>
            <div className="text-[11px] text-muted">{label}</div>
          </Card>
        ))}
      </div>

      <Card className="mt-6 overflow-x-auto p-0">
        <table className="w-full min-w-[880px] text-left text-[13px]">
          <thead>
            <tr className="border-b border-line text-[11px] tracking-wide text-muted uppercase">
              <th className="px-4 py-3">Job</th>
              <th className="px-4 py-3">Project</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Mode</th>
              <th className="px-4 py-3">Platform</th>
              <th className="px-4 py-3">Updated</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.jobs.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-muted">
                  No jobs yet.
                </td>
              </tr>
            )}
            {data.jobs.map((j) => {
              const active =
                j.status === "queued" ||
                j.status === "scripting" ||
                j.status === "rendering";
              return (
                <tr key={j.id} className="border-b border-line/60">
                  <td className="px-4 py-3 font-mono text-[12px]">{j.id}</td>
                  <td className="max-w-44 truncate px-4 py-3">
                    {j.projectName}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={TONE[j.status]}>
                      {j.status}
                      {active ? ` ${j.progress}%` : ""}
                    </Badge>
                    {j.error && (
                      <div className="mt-1 max-w-56 truncate text-[11px] text-[var(--danger)]">
                        {j.error}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={j.mode === "real" ? "accent" : "neutral"}>
                      {j.mode}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-muted">
                    <div>
                      {j.platform} · {j.avatarId.replace("av_", "")}
                      {j.varyHook ? " · hookB" : ""}
                    </div>
                    <div className="text-[11px] text-white/35">
                      {j.templateName}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-muted">
                    {new Date(j.updatedAt).toLocaleTimeString("en-US")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {active && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={busy === j.id}
                        onClick={() => void act(j.id, "cancel")}
                      >
                        Cancel
                      </Button>
                    )}
                    {j.status === "failed" && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={busy === j.id}
                        onClick={() => void act(j.id, "requeue")}
                      >
                        Requeue
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
